import { Colors, Heading, Icon } from '@amelio/ui-library';

import type { PropsWithChildren, SyntheticEvent } from 'react';

type CollapsibleSectionHeadingProps = PropsWithChildren<{
    heading: string;
    isCollapsed: boolean;
    onToggleCollapse?: () => void;
}>;

/**
 * The title row of a section of the Templates page: the heading folds and unfolds the section,
 * `children` holds whatever action the section offers on the right. A folded section offers
 * nothing, so its actions wait until it is open.
 */
export const CollapsibleSectionHeading = ({
    heading,
    isCollapsed,
    onToggleCollapse,
    children,
}: CollapsibleSectionHeadingProps) => {
    // The card around the section folds it on any click; the heading handles its own, so the two
    // would otherwise cancel each other out.
    const handleToggleClick = (event: SyntheticEvent) => {
        event.stopPropagation();
        onToggleCollapse?.();
    };

    return (
        <div className={'flex flex-col justify-between md:flex-row'}>
            <button
                type={'button'}
                className={'flex cursor-pointer items-center gap-2 border-none bg-transparent p-0'}
                onClick={handleToggleClick}
                aria-expanded={!isCollapsed}
            >
                <Heading
                    level={3}
                    weight={'semibold'}
                >
                    {heading}
                </Heading>

                <Icon
                    name={isCollapsed ? 'arrow-arrow-down' : 'arrow-arrow-up-1'}
                    size={'sm'}
                    color={Colors.BASE_600}
                    className={'transition-transform duration-200'}
                />
            </button>

            {!isCollapsed && children}
        </div>
    );
};
